import React, { useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Percent, DollarSign, Settings, Save, Loader2, 
  Building2, Check, X, RefreshCw, AlertCircle, Info
} from 'lucide-react';

const CommissionSettingsTab = ({
  commissionSettings,
  commissionStats,
  operators,
  savingCommission,
  editingOperatorCommission,
  newCommissionRate,
  setEditingOperatorCommission,
  setNewCommissionRate,
  handleUpdateGlobalCommission,
  handleUpdateOperatorCommission,
  handleApplyDefaultToAll,
  getStatusColor
}) => {
  const [localSettings, setLocalSettings] = useState({
    defaultRate: commissionSettings?.defaultRate || 12,
    minRate: commissionSettings?.minRate || 5,
    maxRate: commissionSettings?.maxRate || 30
  });

  // Update local settings when props change
  React.useEffect(() => {
    if (commissionSettings) {
      setLocalSettings({
        defaultRate: commissionSettings.defaultRate || 12,
        minRate: commissionSettings.minRate || 5,
        maxRate: commissionSettings.maxRate || 30
      });
    }
  }, [commissionSettings]);

  const approvedOperators = operators.filter(op => op.status === 'approved');

  return (
    <div className="space-y-6" data-testid="commission-settings-tab">
      {/* Info Banner */}
      <Card className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Percent className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-900">Commission Settings</h3>
            <p className="text-sm text-purple-700 mt-1">
              Configure platform commission rates for bus ticket sales. Set a global default rate or customize rates per operator.
            </p>
          </div>
        </div>
      </Card>

      {/* Stats Overview */}
      {commissionStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Building2 className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Operators</p>
                <p className="text-xl font-bold">{commissionStats.totalOperators}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Percent className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Average Rate</p>
                <p className="text-xl font-bold">{commissionStats.averageRate}%</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rate Range</p>
                <p className="text-xl font-bold">{commissionStats.minOperatorRate}% - {commissionStats.maxOperatorRate}%</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Using Default</p>
                <p className="text-xl font-bold">{commissionStats.operatorsUsingDefault}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Global Commission Settings */}
      <Card className="p-6" data-testid="global-commission-card">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-5 w-5 text-indigo-600" />
          <h3 className="font-semibold text-lg">Global Commission Settings</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Rate (%)
            </label>
            <Input
              type="number"
              value={localSettings.defaultRate}
              onChange={(e) => setLocalSettings({ ...localSettings, defaultRate: parseFloat(e.target.value) || 0 })}
              min={0}
              max={50}
              step={0.5}
              className="w-full"
              data-testid="default-rate-input"
            />
            <p className="text-xs text-gray-500 mt-1">Applied to new operators</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Rate (%)
            </label>
            <Input
              type="number"
              value={localSettings.minRate}
              onChange={(e) => setLocalSettings({ ...localSettings, minRate: parseFloat(e.target.value) || 0 })}
              min={0}
              max={localSettings.defaultRate}
              step={0.5}
              className="w-full"
              data-testid="min-rate-input"
            />
            <p className="text-xs text-gray-500 mt-1">Lowest allowed rate</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Rate (%)
            </label>
            <Input
              type="number"
              value={localSettings.maxRate}
              onChange={(e) => setLocalSettings({ ...localSettings, maxRate: parseFloat(e.target.value) || 0 })}
              min={localSettings.defaultRate}
              max={50}
              step={0.5}
              className="w-full"
              data-testid="max-rate-input"
            />
            <p className="text-xs text-gray-500 mt-1">Highest allowed rate</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => handleUpdateGlobalCommission(localSettings.defaultRate, localSettings.minRate, localSettings.maxRate)}
            disabled={savingCommission}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-global-commission-btn"
          >
            {savingCommission ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>

          <Button
            onClick={handleApplyDefaultToAll}
            variant="outline"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="apply-default-all-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Apply Default to All Without Custom Rate
          </Button>
        </div>

        {commissionSettings && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center gap-2">
            <Info className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              Current default rate: <strong>{commissionSettings.defaultRate}%</strong> | 
              Range: <strong>{commissionSettings.minRate}% - {commissionSettings.maxRate}%</strong>
            </span>
          </div>
        )}
      </Card>

      {/* Per-Operator Commission Rates */}
      <Card className="p-6" data-testid="operator-commission-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            <h3 className="font-semibold text-lg">Operator Commission Rates</h3>
          </div>
          <span className="text-sm text-gray-500">{approvedOperators.length} approved operators</span>
        </div>

        {approvedOperators.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No approved operators yet</p>
            <p className="text-sm text-gray-400">Operators will appear here once approved</p>
          </div>
        ) : (
          <div className="space-y-3">
            {approvedOperators.map((operator) => (
              <div
                key={operator.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid={`operator-commission-row-${operator.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{operator.companyName}</h4>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(operator.status)}`}>
                      {operator.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{operator.email}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {operator.busCount || 0} buses • {operator.routeCount || 0} routes • {operator.bookingCount || 0} bookings
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {editingOperatorCommission === operator.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={newCommissionRate}
                        onChange={(e) => setNewCommissionRate(e.target.value)}
                        placeholder="Rate %"
                        min={commissionSettings?.minRate || 0}
                        max={commissionSettings?.maxRate || 50}
                        step={0.5}
                        className="w-24"
                        data-testid={`operator-rate-input-${operator.id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdateOperatorCommission(operator.id, parseFloat(newCommissionRate))}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`save-operator-rate-${operator.id}`}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingOperatorCommission(null);
                          setNewCommissionRate('');
                        }}
                        data-testid={`cancel-operator-rate-${operator.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="text-right mr-2">
                        <p className="text-lg font-bold text-indigo-600">
                          {operator.commissionRate !== undefined ? `${operator.commissionRate}%` : `${commissionSettings?.defaultRate || 12}%`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {operator.commissionRate !== undefined ? 'Custom' : 'Default'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingOperatorCommission(operator.id);
                          setNewCommissionRate(operator.commissionRate?.toString() || '');
                        }}
                        data-testid={`edit-operator-rate-${operator.id}`}
                      >
                        <Percent className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Commission Calculation Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">How Commission Works</h4>
            <ul className="text-sm text-blue-800 mt-2 space-y-1">
              <li>• Commission is calculated as a percentage of the ticket price</li>
              <li>• Operators receive (100% - commission rate) of each booking</li>
              <li>• Custom rates override the default rate for specific operators</li>
              <li>• Changes apply to new bookings only (existing bookings unchanged)</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CommissionSettingsTab;
